import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Facebook, Send, Loader2 } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Full / Partial Rewire",
  "Fuse Board Upgrade",
  "Lighting Install",
  "EV Charger",
  "EICR / Safety Report",
  "Sockets & Switches",
  "Emergency Callout",
  "Commercial Work",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", postcode: "", service: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Thanks! Ernest will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", postcode: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0A] py-28 lg:py-36 overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#1E90FF]/8 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — heading + contact details */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#1E90FF]">
                04 — Contact
              </span>
              <span className="h-px w-12 bg-[#1E90FF]/40" />
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl text-white font-semibold leading-[1.05] tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Let's get the
              <br />
              <span className="text-[#A0AAB2] italic font-light">job sorted.</span>
            </h2>
            <p className="mt-6 text-[#A0AAB2] leading-relaxed max-w-md">
              Quick reply, no obligation. Tell us about the work and we'll
              come back with honest advice and a clear price.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                data-testid="contact-phone"
                className="flex items-center gap-5 group"
              >
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center group-hover:border-[#1E90FF] group-hover:bg-[#1E90FF]/5 transition-all duration-300">
                  <Phone className="w-4 h-4 text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] text-[#A0AAB2] uppercase">Phone</div>
                  <div className="text-white font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>
                    {BUSINESS.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${BUSINESS.email}`}
                data-testid="contact-email"
                className="flex items-center gap-5 group"
              >
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center group-hover:border-[#1E90FF] group-hover:bg-[#1E90FF]/5 transition-all duration-300">
                  <Mail className="w-4 h-4 text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] text-[#A0AAB2] uppercase">Email</div>
                  <div className="text-white font-medium mt-0.5 text-sm break-all" style={{ fontFamily: "Outfit" }}>
                    {BUSINESS.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-5">
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] text-[#A0AAB2] uppercase">Area</div>
                  <div className="text-white font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>
                    Medway & surrounding Kent
                  </div>
                </div>
              </div>

              <a
                href={BUSINESS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="flex items-center gap-5 group"
              >
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center group-hover:border-[#1E90FF] group-hover:bg-[#1E90FF]/5 transition-all duration-300">
                  <Facebook className="w-4 h-4 text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] text-[#A0AAB2] uppercase">Facebook</div>
                  <div className="text-white font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>
                    @OptimaElectricalSolutionsLtd
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="contact-form"
              className="bg-[#0F0F0F] border border-white/5 p-8 lg:p-12"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Full name" required>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-input-name"
                    className="form-input"
                    required
                  />
                </Field>

                <Field label="Phone" required>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-input-phone"
                    className="form-input"
                    required
                  />
                </Field>

                <Field label="Email">
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className="form-input"
                  />
                </Field>

                <Field label="Postcode">
                  <input
                    name="postcode"
                    value={form.postcode}
                    onChange={onChange}
                    data-testid="contact-input-postcode"
                    className="form-input"
                    placeholder="e.g. ME4 4TR"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Service required">
                    <select
                      name="service"
                      value={form.service}
                      onChange={onChange}
                      data-testid="contact-input-service"
                      className="form-input appearance-none cursor-pointer"
                    >
                      <option value="" style={{ background: "#0F0F0F" }}>Select a service…</option>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o} style={{ background: "#0F0F0F" }}>{o}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="Tell us about the job" required>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      data-testid="contact-input-message"
                      rows={5}
                      className="form-input resize-none"
                      required
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-8 w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#1E90FF] hover:bg-[#0073E6] text-white font-medium tracking-wide transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                ) : (
                  <>Send enquiry <Send className="w-4 h-4" /></>
                )}
              </button>

              <p className="mt-4 text-xs text-[#A0AAB2]/70 text-center">
                We'll get back to you within a few hours, often sooner.
              </p>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(255,255,255,0.12);
          color: #F8F9FA;
          padding: 12px 0;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color 0.3s ease;
        }
        .form-input:focus {
          border-bottom-color: #1E90FF;
        }
        .form-input::placeholder { color: rgba(160,170,178,0.5); }
        select.form-input option { color: #F8F9FA; }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.25em] text-[#A0AAB2] uppercase block mb-2">
      {label} {required && <span className="text-[#1E90FF]">*</span>}
    </span>
    {children}
  </label>
);
